import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const TermsPage = () => (
  <div className="legal-page">
    <div className="legal-container">
      <Link to="/" className="legal-back-link">← Back to Home</Link>

      <div className="legal-header">
        <span className="legal-eyebrow">Legal</span>
        <h1 className="legal-title">Terms & Conditions</h1>
        <p className="legal-updated">Last updated: 18 March 2026</p>
      </div>

      <div className="legal-body">
        <p>
          Welcome to CodeHub (<a href="https://codehub.batosit.com">codehub.batosit.com</a>), an online internship and
          project learning platform operated by <strong>Batos IT Solutions LLP</strong>. These Terms & Conditions govern
          your access to the website, account registration, internship enrollment, payments, project submission workflow,
          and related services offered through the platform. By browsing the website, creating an account, enrolling in
          any internship, or making a payment, you agree to be bound by these terms.
        </p>

        <h2>1. Introduction</h2>
        <p>
          CodeHub provides structured, project-based internship programs and related educational services for students,
          developers, and learners who want practical implementation experience. The platform allows users to register,
          choose an internship project, make payment online, submit stage-wise work, receive review feedback, and obtain
          a verifiable certificate upon successful completion.
        </p>
        <p>
          These terms form a legally binding agreement between you and Batos IT Solutions LLP. If you do not agree with
          any part of these terms, you should not use the platform or make any payment.
        </p>
        <p>
          These Terms & Conditions should be read together with our <Link to="/privacy">Privacy Policy</Link>,{' '}
          <Link to="/refund-policy">Refund Policy</Link>, and <Link to="/cancellation-policy">Cancellation Policy</Link>.
        </p>

        <h2>2. Business Details</h2>
        <ul>
          <li><strong>Business Name:</strong> Batos IT Solutions LLP</li>
          <li><strong>Brand / Platform Name:</strong> CodeHub</li>
          <li><strong>Website:</strong> <a href="https://www.codehub.batosit.com">www.codehub.batosit.com</a></li>
          <li><strong>Business Type:</strong> Online internship, project learning, mentoring, and certificate issuance platform</li>
          <li><strong>Operating Address:</strong> Sterling Towers, Level 1, 2 & 3, Pan Card Club Road, Baner, Pune 411045, Maharashtra, India</li>
          <li><strong>Support Email:</strong> <a href="mailto:support@batosit.com">support@batosit.com</a></li>
        </ul>

        <h2>3. Services Offered</h2>
        <p>Through CodeHub, we offer the following services:</p>
        <ul>
          <li>Project-based internship enrollment in selected technology categories</li>
          <li>Stage-wise project instructions and submission workflow</li>
          <li>Review and evaluation of submitted project stages</li>
          <li>Mentor guidance through the support section for doubts and blockers</li>
          <li>Internship completion certificates for eligible users who successfully complete the program</li>
        </ul>
        <p>
          We reserve the right to modify, update, suspend, or discontinue any internship category, project, feature,
          or service at any time without prior notice.
        </p>
        <p>
          CodeHub provides <strong>digital services only</strong>. We do not sell or ship any physical products. Access to
          internship materials, project instructions, dashboards, and submission features is delivered electronically
          through your registered account on the website.
        </p>

        <h2>4. Eligibility and Account Registration</h2>
        <p>
          The platform is open to students, developers, and programming enthusiasts. By registering, you confirm that you
          are at least 16 years of age and have the legal capacity to enter into this agreement. Users must provide
          accurate and complete information during registration, including a valid email address and phone number.
          You are responsible for maintaining the confidentiality of your account credentials and for all activity
          conducted through your account.
        </p>

        <h2>5. Internship Enrollment and Access</h2>
        <ul>
          <li>Students may enroll in only <strong>one project internship</strong> at a time.</li>
          <li>Once a project is selected and enrollment is confirmed, the project selection <strong>cannot be changed</strong>.</li>
          <li>Enrollment is confirmed only after successful payment.</li>
          <li>Access to paid internship content is provided only to the enrolled user and must not be shared with others.</li>
        </ul>

        <h2>6. Service Fulfilment and Delivery</h2>
        <p>
          Since CodeHub is a digital internship platform, service fulfilment takes place online. After a successful payment,
          the user is granted access to the selected internship project, subject to account status and platform availability.
        </p>
        <ul>
          <li>Access is typically enabled immediately or within a short time after successful payment confirmation.</li>
          <li>Internship instructions, submission workflow, review updates, and certificate availability are delivered electronically through the website and registered email.</li>
          <li>No courier, shipping, or physical delivery is involved in the normal course of service fulfilment.</li>
        </ul>

        <h2>7. Internship Structure, Reviews, and Certification</h2>
        <ul>
          <li>Each internship project is divided into multiple stages that must be completed <strong>sequentially</strong>.</li>
          <li>Students must successfully complete and submit each stage before progressing to the next.</li>
          <li>All stages must be completed within the internship duration to be eligible for certification.</li>
          <li>All project submissions are reviewed by the CodeHub review team, and users may be asked to revise or resubmit work.</li>
          <li>Certificates are issued only after successful completion and approval of the required internship stages.</li>
        </ul>

        <h2>8. User Responsibilities</h2>
        <ul>
          <li>You must provide true, current, and complete information while using the platform.</li>
          <li>The internship project must be completed by the enrolled user <strong>individually</strong>.</li>
          <li>Plagiarism, impersonation, copying, or submitting work that is not your own may lead to rejection, disqualification, or account suspension.</li>
          <li>You must not misuse the website, attempt unauthorized access, interfere with platform operations, or use the platform for unlawful purposes.</li>
          <li>Any use of AI tools or external resources must still result in work that you understand and can explain if requested.</li>
        </ul>

        <h2>9. Payment Terms</h2>
        <p>
          All payments on CodeHub are processed securely through <strong>Razorpay</strong>. Supported payment methods may include
          UPI, debit cards, credit cards, net banking, wallets, and other methods made available by Razorpay and participating banks.
        </p>
        <ul>
          <li>All fees are displayed in <strong>Indian Rupees (INR)</strong>.</li>
          <li>Prices are displayed in Indian Rupees (INR) and include all applicable taxes.</li>
          <li>Enrollment is confirmed only after successful authorization and capture of payment.</li>
          <li>If a transaction is declined by the issuing bank, payment network, or gateway, the enrollment will not be confirmed until a successful payment is received.</li>
          <li>You are responsible for ensuring that your chosen payment instrument is valid, sufficiently funded, and enabled for online use.</li>
          <li>Payment confirmation, order reference, or transaction acknowledgment may be shown on the platform and/or sent to your registered email address.</li>
        </ul>

        <div className="legal-highlight">
          <strong>⚠️ Important Payment Notice:</strong> Successful payment does not automatically guarantee certificate issuance.
          Certificates are awarded only after successful internship completion and approval of all required stages.
        </div>

        <h2>10. Refund, Cancellation, and Project Change Policy</h2>
        <p>
          All enrollment payments are subject to our published refund and cancellation rules. Users are required to review
          these policies before making a payment.
        </p>
        <ul>
          <li><strong>Refund Policy:</strong> <Link to="/refund-policy">View Refund Policy</Link></li>
          <li><strong>Cancellation Policy:</strong> <Link to="/cancellation-policy">View Cancellation Policy</Link></li>
        </ul>
        <p>
          In general, internship enrollments are final after successful payment, and project selections cannot be changed
          once enrollment is confirmed. Please read the linked policy pages carefully before proceeding.
        </p>

        <h2>11. Privacy Policy</h2>
        <p>
          Your use of the platform is also governed by our <Link to="/privacy">Privacy Policy</Link>, which explains how
          we collect, use, store, and protect your personal information, including limited payment-related metadata and
          account activity information.
        </p>

        <h2>12. Intellectual Property</h2>
        <ul>
          <li>All website content, internship materials, project guidelines, branding, text, graphics, and platform resources are owned by or licensed to Batos IT Solutions LLP.</li>
          <li>Paid or restricted materials may not be copied, redistributed, republished, sold, or shared without prior written permission.</li>
          <li>Users retain ownership of the original code and project work they personally create, subject to any third-party licensing obligations applicable to their submissions.</li>
        </ul>

        <h2>13. Account Suspension and Termination</h2>
        <p>
          We reserve the right to suspend, restrict, or terminate access to the platform if a user violates these terms,
          engages in fraud, abuses payment systems, attempts unauthorized access, or misuses the internship workflow.
          Suspension or termination for policy violations does not create any right to refund.
        </p>

        <h2>14. Limitation of Liability</h2>
        <p>
          CodeHub and all related services are provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis.
          To the maximum extent permitted by law, Batos IT Solutions LLP shall not be liable for any indirect, incidental,
          special, consequential, or punitive damages arising out of or related to your use of the platform, including but
          not limited to payment failures caused by banks, internet issues, service interruptions, temporary downtime,
          delays in review timelines, loss of opportunity, or loss of data.
        </p>
        <p>
          Our total liability, if any, shall not exceed the amount actually paid by you for the specific internship service
          giving rise to the claim.
        </p>

        <h2>15. Governing Law</h2>
        <p>
          These Terms & Conditions shall be governed by and construed in accordance with the laws of India.
          Any dispute, claim, or legal proceeding arising out of or relating to these terms or your use of the platform
          shall be subject to the exclusive jurisdiction of the courts located in <strong>Pune, Maharashtra, India</strong>.
        </p>

        <h2>16. Changes to Terms</h2>
        <p>
          We may update these Terms & Conditions from time to time to reflect operational, legal, or service-related changes.
          Updated versions will be posted on this page with a revised effective date. Continued use of the platform after
          such updates constitutes acceptance of the revised terms.
        </p>

        <div className="legal-contact">
          <h3>17. Contact Information</h3>
          <p>
            <strong>Batos IT Solutions LLP</strong><br />
            Sterling Towers, Level 1, 2 & 3, Pan Card Club Road, Baner, Pune 411045, Maharashtra, India<br />
            Email: <a href="mailto:support@batosit.com">support@batosit.com</a><br />
            Website: <a href="https://www.codehub.batosit.com">www.codehub.batosit.com</a>
          </p>
        </div>

        <h2>18. Last Updated Date</h2>
        <p>
          These Terms & Conditions were last updated on <strong>18 March 2026</strong>.
        </p>
      </div>
    </div>
  </div>
);

export default TermsPage;
